import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Inspection,
  InspectionListItem,
  InspectionFilters,
  InspectionFinding,
  PreviousUnresolvedFinding,
  UpdateFindingInput,
  InspectionCollaborator,
  InProgressInspection,
  ResolveWithEvidenceInput,
  getInspections,
  getInspection,
  updateFinding,
  deleteFinding,
  restoreFinding,
  getPreviousUnresolvedFindings,
  rediscoverFinding,
  completeReview,
  isAnalysisComplete,
  // 공동 점검 관련
  getInProgressForSite,
  joinInspection,
  leaveInspection,
  completeMyReview,
  resolveWithEvidence,
  getContributors,
  // 사진 검토 관련
  updatePhotoReview,
  bulkReviewPhotos,
  // 개선대책/관련법규 관련
  addMeasure,
  removeMeasure,
  addLegalBasis,
  removeLegalBasis,
} from '../lib/inspections';

/**
 * 점검 목록 조회 훅
 */
export function useInspections(
  companyId: string | undefined,
  filters?: InspectionFilters
) {
  return useQuery({
    queryKey: ['inspections', companyId, filters?.siteId, filters?.status],
    queryFn: async () => {
      const response = await getInspections(companyId!, filters);
      return response.data || [];
    },
    enabled: !!companyId,
  });
}

/**
 * 현장별 점검 목록 조회 훅
 */
export function useSiteInspections(
  companyId: string | undefined,
  siteId: string | undefined,
  filters?: Omit<InspectionFilters, 'siteId'>
) {
  return useQuery({
    queryKey: ['inspections', siteId],
    queryFn: async () => {
      const response = await getInspections(companyId!, { ...filters, siteId });
      return response.data || [];
    },
    enabled: !!companyId && !!siteId,
  });
}

/**
 * 점검 상세 조회 훅
 * - refetchOnMount: 'always' - 페이지 진입 시 항상 새 데이터 조회
 *   (현장 담당자 추가 등 권한 변경 시 캐시된 accessType이 outdated될 수 있음)
 * - refetchOnWindowFocus: true - 탭 전환 후 돌아오면 최신 데이터 조회
 * - refetchInterval: 분석 중인 사진이 있을 때만 5초 폴링 (WebSocket 이벤트 유실 대비)
 */
export function useInspection(
  inspectionId: string | undefined,
  companyId: string | undefined
) {
  return useQuery({
    queryKey: ['inspection', inspectionId],
    queryFn: async () => {
      const response = await getInspection(inspectionId!, companyId!);
      return response.data!;
    },
    enabled: !!inspectionId && !!companyId,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data?.photos) return false;
      const hasAnalyzing = data.photos.some(
        (p) => p.analysisStatus === 'ANALYZING' || p.analysisStatus === 'PENDING'
      );
      return hasAnalyzing ? 5000 : false;
    },
  });
}

/**
 * 발견사항 수정 훅
 */
export function useUpdateFinding(
  inspectionId: string | undefined,
  companyId: string | undefined
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ findingId, data }: { findingId: string; data: UpdateFindingInput }) => {
      if (!inspectionId || !companyId) {
        throw new Error('inspectionId and companyId are required');
      }
      const response = await updateFinding(inspectionId, findingId, data, companyId);
      if (!response.success) {
        throw new Error(response.error?.message || '발견사항 수정에 실패했습니다.');
      }
      return response.data!;
    },
    onSuccess: () => {
      // 캐시 무효화는 WebSocket 이벤트에서 처리하므로 여기서는 생략 가능
      // 하지만 즉각적인 UI 반영을 위해 무효화 추가
      queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId] });
    },
  });
}

/**
 * 발견사항 삭제 훅
 */
export function useDeleteFinding(
  inspectionId: string | undefined,
  companyId: string | undefined
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (findingId: string) => {
      if (!inspectionId || !companyId) {
        throw new Error('inspectionId and companyId are required');
      }
      const response = await deleteFinding(inspectionId, findingId, companyId);
      if (!response.success) {
        throw new Error(response.error?.message || '발견사항 삭제에 실패했습니다.');
      }
      return response.data!;
    },
    onSuccess: () => {
      // 캐시 무효화는 WebSocket 이벤트에서 처리하므로 여기서는 생략 가능
      // 하지만 즉각적인 UI 반영을 위해 무효화 추가
      queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId] });
    },
  });
}

/**
 * 발견사항 복원 훅
 */
export function useRestoreFinding(
  inspectionId: string | undefined,
  companyId: string | undefined
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (findingId: string) => {
      if (!inspectionId || !companyId) {
        throw new Error('inspectionId and companyId are required');
      }
      const response = await restoreFinding(inspectionId, findingId, companyId);
      if (!response.success) {
        throw new Error(response.error?.message || '발견사항 복원에 실패했습니다.');
      }
      return response.data!;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId] });
    },
  });
}

/**
 * 이전 미해결 발견사항 조회 훅
 */
export function usePreviousUnresolvedFindings(
  inspectionId: string | undefined,
  companyId: string | undefined
) {
  return useQuery({
    queryKey: ['previousFindings', inspectionId],
    queryFn: async () => {
      const response = await getPreviousUnresolvedFindings(inspectionId!, companyId!);
      return response.data || [];
    },
    enabled: !!inspectionId && !!companyId,
  });
}

/**
 * 발견사항 재발견 훅
 */
export function useRediscoverFinding(
  inspectionId: string | undefined,
  companyId: string | undefined
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (originalFindingId: string) => {
      if (!inspectionId || !companyId) {
        throw new Error('inspectionId and companyId are required');
      }
      const response = await rediscoverFinding(inspectionId, originalFindingId, companyId);
      if (!response.success) {
        throw new Error(response.error?.message || '재발견 처리에 실패했습니다.');
      }
      return response.data!;
    },
    onSuccess: () => {
      // 현재 점검과 이전 미해결 발견사항 목록 캐시 무효화
      queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId] });
      queryClient.invalidateQueries({ queryKey: ['previousFindings', inspectionId] });
    },
  });
}

/**
 * AI 분석 완료 여부 확인 훅
 */
export function useIsAnalysisComplete(
  inspectionId: string | undefined,
  companyId: string | undefined
) {
  return useQuery({
    queryKey: ['analysisComplete', inspectionId],
    queryFn: async () => {
      const response = await isAnalysisComplete(inspectionId!, companyId!);
      return response.data?.isComplete ?? false;
    },
    enabled: !!inspectionId && !!companyId,
    refetchInterval: (query) => {
      // 분석 완료되면 polling 중지
      if (query.state.data === true) {
        return false;
      }
      return 5000; // 5초마다 확인
    },
  });
}

/**
 * 검수 완료 훅
 */
export function useCompleteReview(
  inspectionId: string | undefined,
  companyId: string | undefined
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (!inspectionId || !companyId) {
        throw new Error('inspectionId and companyId are required');
      }
      const response = await completeReview(inspectionId, companyId);
      if (!response.success) {
        throw new Error(response.error?.message || '검수 완료에 실패했습니다.');
      }
      return response.data!;
    },
    onSuccess: () => {
      // 캐시 무효화
      queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId] });
      queryClient.invalidateQueries({ queryKey: ['inspections'] });
    },
  });
}

// ============================================
// 공동 점검 관련 훅
// ============================================

/**
 * 현장별 진행 중 점검 조회 훅
 */
export function useInProgressInspections(
  siteId: string | undefined,
  companyId: string | undefined
) {
  return useQuery({
    queryKey: ['inspections', 'in-progress', siteId],
    queryFn: async () => {
      const response = await getInProgressForSite(siteId!, companyId!);
      return response.data || [];
    },
    enabled: !!siteId && !!companyId,
  });
}

/**
 * 점검 기여자 목록 조회 훅
 */
export function useContributors(
  inspectionId: string | undefined,
  companyId: string | undefined
) {
  return useQuery({
    queryKey: ['inspection', inspectionId, 'contributors'],
    queryFn: async () => {
      const response = await getContributors(inspectionId!, companyId!);
      return response.data || [];
    },
    enabled: !!inspectionId && !!companyId,
  });
}

/**
 * 점검 참여 훅 (함께 점검)
 */
export function useJoinInspection(companyId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (inspectionId: string) => {
      if (!companyId) {
        throw new Error('companyId is required');
      }
      const response = await joinInspection(inspectionId, companyId);
      if (!response.success) {
        throw new Error(response.error?.message || '점검 참여에 실패했습니다.');
      }
      return response.data!;
    },
    onSuccess: (_data, inspectionId) => {
      // 캐시 무효화
      queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId] });
      queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId, 'contributors'] });
      queryClient.invalidateQueries({ queryKey: ['inspections', 'in-progress'] });
    },
  });
}

/**
 * 점검 나가기 훅
 */
export function useLeaveInspection(companyId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (inspectionId: string) => {
      if (!companyId) {
        throw new Error('companyId is required');
      }
      const response = await leaveInspection(inspectionId, companyId);
      if (!response.success) {
        throw new Error(response.error?.message || '점검 나가기에 실패했습니다.');
      }
      return response.data!;
    },
    onSuccess: (_data, inspectionId) => {
      // 캐시 무효화
      queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId] });
      queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId, 'contributors'] });
      queryClient.invalidateQueries({ queryKey: ['inspections', 'in-progress'] });
    },
  });
}

/**
 * 내 검수 완료 훅
 */
export function useCompleteMyReview(
  inspectionId: string | undefined,
  companyId: string | undefined
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (!inspectionId || !companyId) {
        throw new Error('inspectionId and companyId are required');
      }
      const response = await completeMyReview(inspectionId, companyId);
      if (!response.success) {
        throw new Error(response.error?.message || '검수 완료에 실패했습니다.');
      }
      return response.data!;
    },
    onSuccess: () => {
      // 캐시 무효화
      queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId] });
      queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId, 'contributors'] });
    },
  });
}

/**
 * 개별 사진 검토 상태 업데이트 훅
 */
export function useUpdatePhotoReview(
  inspectionId: string | undefined,
  companyId: string | undefined
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ photoId, isReviewed }: { photoId: string; isReviewed: boolean }) => {
      if (!inspectionId || !companyId) {
        throw new Error('inspectionId and companyId are required');
      }
      const response = await updatePhotoReview(inspectionId, photoId, isReviewed, companyId);
      if (!response.success) {
        throw new Error(response.error?.message || '검토 상태 변경에 실패했습니다.');
      }
      return response.data!;
    },
    onSuccess: () => {
      // 캐시 무효화
      queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId] });
    },
  });
}

/**
 * 일괄 검토 완료 훅
 */
export function useBulkReviewPhotos(
  inspectionId: string | undefined,
  companyId: string | undefined
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (!inspectionId || !companyId) {
        throw new Error('inspectionId and companyId are required');
      }
      const response = await bulkReviewPhotos(inspectionId, companyId);
      if (!response.success) {
        throw new Error(response.error?.message || '일괄 검토에 실패했습니다.');
      }
      return response.data!;
    },
    onSuccess: () => {
      // 캐시 무효화
      queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId] });
    },
  });
}

/**
 * 발견사항 해결 (증빙 포함) 훅
 */
export function useResolveWithEvidence(
  inspectionId: string | undefined,
  companyId: string | undefined
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      findingId,
      data,
    }: {
      findingId: string;
      data: ResolveWithEvidenceInput;
    }) => {
      if (!inspectionId || !companyId) {
        throw new Error('inspectionId and companyId are required');
      }
      const response = await resolveWithEvidence(inspectionId, findingId, data, companyId);
      if (!response.success) {
        throw new Error(response.error?.message || '발견사항 해결에 실패했습니다.');
      }
      return response.data!;
    },
    onSuccess: () => {
      // 캐시 무효화
      queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId] });
      queryClient.invalidateQueries({ queryKey: ['previousFindings', inspectionId] });
    },
  });
}

// ============================================
// 개선대책/관련법규 관련 훅
// ============================================

/**
 * 개선대책 추가 훅
 * @param measureId HazardKnowledge ID (선택 시 법적 근거 연동)
 */
export function useAddMeasure(
  inspectionId: string | undefined,
  companyId: string | undefined
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ findingId, measure, measureId }: { findingId: string; measure: string; measureId?: string }) => {
      if (!inspectionId || !companyId) {
        throw new Error('inspectionId and companyId are required');
      }
      const response = await addMeasure(inspectionId, findingId, measure, companyId, measureId);
      if (!response.success) {
        throw new Error(response.error?.message || '개선대책 추가에 실패했습니다.');
      }
      return response.data!;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId] });
    },
  });
}

/**
 * 개선대책 삭제 훅
 */
export function useRemoveMeasure(
  inspectionId: string | undefined,
  companyId: string | undefined
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ findingId, index }: { findingId: string; index: number }) => {
      if (!inspectionId || !companyId) {
        throw new Error('inspectionId and companyId are required');
      }
      const response = await removeMeasure(inspectionId, findingId, index, companyId);
      if (!response.success) {
        throw new Error(response.error?.message || '개선대책 삭제에 실패했습니다.');
      }
      return response.data!;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId] });
    },
  });
}

/**
 * 관련법규 추가 훅
 * @param selectedMeasures 연결할 개선대책 배열 (피드백 생성용)
 */
export function useAddLegalBasis(
  inspectionId: string | undefined,
  companyId: string | undefined
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      findingId,
      legalBasis,
      selectedMeasures,
    }: {
      findingId: string;
      legalBasis: { type: string; article?: string; summary: string; url?: string; regulationId?: string };
      selectedMeasures?: string[];
    }) => {
      if (!inspectionId || !companyId) {
        throw new Error('inspectionId and companyId are required');
      }
      const response = await addLegalBasis(inspectionId, findingId, legalBasis, companyId, selectedMeasures);
      if (!response.success) {
        throw new Error(response.error?.message || '관련법규 추가에 실패했습니다.');
      }
      return response.data!;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId] });
    },
  });
}

/**
 * 관련법규 삭제 훅
 */
export function useRemoveLegalBasis(
  inspectionId: string | undefined,
  companyId: string | undefined
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ findingId, index }: { findingId: string; index: number }) => {
      if (!inspectionId || !companyId) {
        throw new Error('inspectionId and companyId are required');
      }
      const response = await removeLegalBasis(inspectionId, findingId, index, companyId);
      if (!response.success) {
        throw new Error(response.error?.message || '관련법규 삭제에 실패했습니다.');
      }
      return response.data!;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId] });
    },
  });
}

// Re-export types for convenience
export type {
  Inspection,
  InspectionListItem,
  InspectionFilters,
  InspectionFinding,
  PreviousUnresolvedFinding,
  UpdateFindingInput,
  InspectionCollaborator,
  InProgressInspection,
  ResolveWithEvidenceInput,
};
